import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Send } from "lucide-react";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { reactionTotal, storyDate, storyReactionOptions } from "../lib/stories";

function ReactionBar({ story, canContribute, onReact, working }) {
  return (
    <div className="story-reactions">
      <div className="story-reactions__summary">
        <strong>{reactionTotal(story.reactionCounts)}</strong>
        <span>{reactionTotal(story.reactionCounts) === 1 ? "reaction" : "reactions"}</span>
      </div>
      <div className="story-reactions__choices" aria-label="React to this story">
        {storyReactionOptions.map((option) => (
          <button
            className={story.myReaction === option.key ? "story-reaction is-active" : "story-reaction"}
            type="button"
            key={option.key}
            disabled={!canContribute || working}
            aria-pressed={story.myReaction === option.key}
            onClick={() => onReact(option.key)}
          >
            <span aria-hidden="true">{option.emoji}</span>
            <small>{option.label}</small>
            {Number(story.reactionCounts?.[option.key]) > 0 && <b>{story.reactionCounts[option.key]}</b>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StoryDetail({ session, onLogin }) {
  const { storyId } = useParams();
  const authenticated = Boolean(session?.authenticated);
  const canContribute = Number(session?.user?.memberId) > 0;
  const [comment, setComment] = useState("");
  const [actionError, setActionError] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [reacting, setReacting] = useState(false);
  const loader = useCallback(
    () => authenticated ? api(`/api/stories/${storyId}`) : Promise.resolve(null),
    [authenticated, storyId],
  );
  const { data, error, loading, setData } = useApi(loader, [loader]);
  const story = data?.story;

  async function react(reaction) {
    setReacting(true);
    setActionError("");
    try {
      const result = await api(`/api/stories/${storyId}/reactions`, { method: "POST", body: JSON.stringify({ reaction }) });
      setData((current) => ({ ...current, story: { ...current.story, ...result } }));
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setReacting(false);
    }
  }

  async function addComment(event) {
    event.preventDefault();
    setCommenting(true);
    setActionError("");
    try {
      const result = await api(`/api/stories/${storyId}/comments`, { method: "POST", body: JSON.stringify({ body: comment }) });
      setData((current) => ({
        ...current,
        story: { ...current.story, comments: [...current.story.comments, result.comment] },
      }));
      setComment("");
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setCommenting(false);
    }
  }

  if (!authenticated) {
    return <div className="stories-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }
  if (loading) return <div className="story-detail-state section-wrap"><Loading label="Opening this story" /></div>;
  if (error || !story) return <div className="story-detail-state section-wrap"><ErrorMessage>{error || "Story not found."}</ErrorMessage><Link className="text-link" to="/stories"><ArrowLeft aria-hidden="true" /> Back to stories</Link></div>;

  return (
    <article className="story-detail">
      <header className="story-detail__header section-wrap">
        <Link className="back-link" to="/stories"><ArrowLeft aria-hidden="true" /> All class stories</Link>
        <p className="kicker">A memory by {story.author.name}</p>
        <h1>{story.title}</h1>
        <div className="story-detail__meta">
          <span>{story.author.branch || "MACE ’95"}</span>
          <span>{storyDate(story.createdAt)}</span>
          <span>{story.comments.length} {story.comments.length === 1 ? "comment" : "comments"}</span>
        </div>
      </header>
      <figure className="story-detail__cover">
        <img src={story.coverUrl} alt={`Cover for ${story.title}`} />
        <figcaption><span>From the MACE 95 living archive</span><span>Story / {story.author.name}</span></figcaption>
      </figure>
      <div className="story-detail__body section-wrap">
        <aside><span aria-hidden="true">“</span><p>Thirty years later, this is how I remember it.</p></aside>
        <p className="story-essay">{story.body}</p>
      </div>

      <section className="story-conversation">
        <div className="section-wrap">
          <ReactionBar story={story} canContribute={canContribute} onReact={react} working={reacting} />
          {!canContribute && <p className="story-account-note">You’re reading with shared class access. <Link to="/members">Sign in with your individual account</Link> to react or join the conversation.</p>}
          {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
          <div className="story-comments">
            <header>
              <p className="kicker">The conversation</p>
              <h2>What this brings back</h2>
              <span><MessageCircle aria-hidden="true" /> {story.comments.length}</span>
            </header>
            {canContribute && (
              <form className="story-comment-form" onSubmit={addComment}>
                <label htmlFor="story-comment">Add your memory or response</label>
                <textarea id="story-comment" required maxLength="1200" rows="4" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="What do you remember?" />
                <div><small>{comment.length} / 1,200</small><button className="button button--ink" disabled={commenting} type="submit">{commenting ? "Posting…" : "Post comment"} <Send aria-hidden="true" /></button></div>
              </form>
            )}
            <div className="story-comment-list">
              {story.comments.length === 0 && <p className="story-comments__empty">No comments yet. The first reply can be a memory of your own.</p>}
              {story.comments.map((item, index) => (
                <article className="story-comment" key={item.id}>
                  <span className="story-comment__index">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <p>{item.body}</p>
                    <footer><strong>{item.author.name}</strong><span>{storyDate(item.createdAt)}</span></footer>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
